/**
 * EconomicModelingScenariosPage -- the FI's view of its own Economic Modeling
 * scenarios (GH #2727).
 *
 * Route: /modeling/scenarios. Reachable only when the tenant carries
 * Institution.enableOverdraftPreview -- one flag, two programs, per the LOCKED
 * semantics block in platform-admin.openapi.yaml (operator ruling 2026-08-26,
 * GO-LIVE-PLAN section 12.5 C2). The nav gate hides it; the API gate 403s it.
 *
 * SCOPE, deliberately. This lists and reads the tenant's scenarios. Scenario
 * AUTHORING stays on the Aaim-operator surface (strategy.aaim.com : 9305) for
 * now: that wizard already exists in apps/strategy-tools, and re-implementing it
 * here would be the parallel-element defect `canon-fit-escalation.md` names,
 * not a second audience for one element. When FI-side authoring is ruled in, it
 * composes the same contract this page reads.
 *
 * CHROME PLACEMENT (GH #2924). The H1 and the subtitle render in the loading,
 * error and loaded states; the not-entitled path returns the warning alone.
 * The skeleton, the error alert, and the scenario list are siblings below them,
 * per KG `technology-innovation/ui-package/2026-05-18-canonical-chrome-renders-outside-error-conditionals.md`.
 * The AppHeader chrome title reads "Economic Modeling" on this route while the
 * read is pending, so a body carrying no heading during the skeleton reports to
 * a DOM-reading proof as a satisfied wait: the td-09 prod runner recorded
 * `observed main headings: []` for this route on 2026-09-22.
 *
 * THE MODELED INSTITUTION RENDERS BY NAME, NEVER BY ID (GH #678, operator
 * ruling 2026-10-23). `ScenarioSummary.institutionId` is the modeled SUBJECT,
 * a market-intelligence catalog reference or an external id, and not a tenant
 * id (economic-modeling.openapi.yaml, info.description "Ownership vs
 * subject"), so a tenant list cannot name it. The name comes from the read
 * keyed by that id, GET /api/v1/modeling/institutions/{id}/profile
 * (getInstitutionProfile, `profile.name`). An id that resolves to no name (a
 * 404, a 422 for missing inputs, a 502, or a read still pending) shows nothing
 * in that place; the id itself is never printed.
 */

import { useQueries } from '@tanstack/react-query';
import { Alert, BodyText, Card, Heading, Skeleton } from '@aaimplatform/ui';
import { useEconomicModelingScenarios } from '../../hooks/useEconomicModelingScenarios';
import { economicModelingClient } from '../../api/client';

function formatTimestamp(iso: string): string {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
}

/**
 * Resolves each distinct modeled-subject id to the name its profile read
 * carries. One read per distinct id, cached across renders; an id whose read
 * fails or carries no name is absent from the returned map.
 */
function useModeledInstitutionNames(institutionIds: readonly string[]): ReadonlyMap<string, string> {
  const distinct = [...new Set(institutionIds.filter((id) => id.trim() !== ''))];
  const results = useQueries({
    queries: distinct.map((id) => ({
      queryKey: ['economic-modeling', 'institution-profile', id],
      queryFn: async (): Promise<string | null> => {
        const { data, response } = await economicModelingClient.GET(
          '/api/v1/modeling/institutions/{id}/profile',
          { params: { path: { id } } },
        );
        if (!response.ok) {
          throw Object.assign(
            new Error(`Failed to load the modeled institution (HTTP ${response.status})`),
            { status: response.status },
          );
        }
        const name = data?.profile?.name?.trim();
        return name ? name : null;
      },
      staleTime: 5 * 60 * 1000,
      // A name is chrome on this page. A 404 or 422 will not change on a retry,
      // and a missing name leaves the row readable.
      retry: false,
    })),
  });
  const names = new Map<string, string>();
  distinct.forEach((id, index) => {
    const name = results[index]?.data;
    if (name) names.set(id, name);
  });
  return names;
}

export default function EconomicModelingScenariosPage() {
  const { data, isLoading, error } = useEconomicModelingScenarios();
  const modeledNames = useModeledInstitutionNames(
    (data?.scenarios ?? []).map((scenario) => scenario.institutionId),
  );

  if (data?.notEntitled) {
    return (
      <Alert type="warning" title="Economic Modeling is not enabled">
        Your institution is not entitled to the Economic Modeling surface.
        Aaim sets the entitlement; ask your Aaim contact to enable it.
      </Alert>
    );
  }

  const scenarios = data?.scenarios ?? [];

  return (
    <div className="space-y-6">
      <div>
        {/* Economic Modeling, never "Aaim Foresight": operator ruling
            2026-09-21 (GH #2915) reserves Foresight for depositor-level
            decisioning (tempo-design.md 12.2). The nav group and this
            heading carry the same label. Level 2: PortalLayout's AppHeader
            renders the page h1 (operator ruling on GH #2664, 2026-08-20). */}
        <Heading level={2} size="page">Economic Modeling</Heading>
        <BodyText>
          Scenarios owned by your institution. Projections, simulation, and
          sensitivity analysis run against the parameters recorded on each one.
        </BodyText>
      </div>

      {isLoading && (
        <div className="space-y-4" data-testid="scenarios-loading">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {error && (
        <Alert type="error" title="Scenarios unavailable">
          {error instanceof Error ? error.message : 'Failed to load scenarios'}
        </Alert>
      )}

      {/* The empty-state card is gated on !error. `scenarios` is [] on the error
          path, so an ungated empty branch would print "No scenarios yet" beside
          the failure alert and state an empty book the server never returned. */}
      {!isLoading &&
        !error &&
        (scenarios.length === 0 ? (
          <Card>
            <Heading level={3}>No scenarios yet</Heading>
            <BodyText>
              No scenarios have been created for your institution. Aaim authors
              scenarios today; ask your Aaim contact to
              create the first one.
            </BodyText>
          </Card>
        ) : (
          <div className="space-y-3">
            {scenarios.map((scenario) => {
              const modeledName = modeledNames.get(scenario.institutionId);
              return (
                <Card key={scenario.id}>
                  <Heading level={3}>{scenario.name}</Heading>
                  {scenario.description ? (
                    <BodyText>{scenario.description}</BodyText>
                  ) : null}
                  <BodyText>
                    {modeledName ? <>Modeled institution: {modeledName} &middot; </> : null}
                    Created {formatTimestamp(scenario.createdAt)} &middot; Updated{' '}
                    {formatTimestamp(scenario.updatedAt)}
                  </BodyText>
                </Card>
              );
            })}
          </div>
        ))}
    </div>
  );
}
